import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { convertESTtoUserLocalDateAndTime } from "helpers/methods";
import { RequestParticipant } from "models/interfaces";
import { setErrors, updateMultipleSigners } from "redux/appSlice";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import approversAndSignersStyles from "../ApproversAndSigners.styles";
import { ClockGlyph, CloseGlyph } from "./RecipientsIcons";

const styles = approversAndSignersStyles;

/** Days the design's − / + steppers can reach. */
const MIN_DELAY_DAYS = 1;
const MAX_DELAY_DAYS = 99;

interface SignerDelaySlotProps {
  /**
   * The signers the wait applies to — the members of the level that runs
   * *after* the gap, which is where the app stores the delay.
   */
  signers: RequestParticipant[];
  dispatch: any;
  /** Opens the date/time picker for an absolute wait. */
  onEdit: () => void;
}

/**
 * "Wait N day(s) before next level" slot that sits between two signing levels,
 * per the design's `g.showDelaySlot` block. Falls back to an "Add delay" dashed
 * pill while no wait is set.
 */
const SignerDelaySlot = ({
  signers,
  dispatch,
  onEdit,
}: SignerDelaySlotProps) => {
  const delayedRoutingState = useFeatureState(
    FeatureKeys.ENVELOPE_DELAYED_ROUTING,
  );
  const isAvailable =
    delayedRoutingState.status === strings.FEATURE_STATUS_AVAILABLE;
  const tooltip =
    delayedRoutingState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((delayedRoutingState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : delayedRoutingState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  /** CC recipients never wait, so they stay out of the delay entirely. */
  const actionable = signers.filter((signer) => !signer.cc);
  const owner = actionable.find(
    (signer) => signer.delayDays || signer.delayHours || signer.scheduleTime,
  );

  const applyToLevel = (fields: Record<string, unknown>) => {
    if (!actionable.length) return;
    dispatch(
      updateMultipleSigners(
        actionable.map((signer) => ({
          signerId: signer.id,
          fieldsToUpdate: fields,
        })),
      ),
    );
  };

  const stepDays = (delta: number) => {
    const current = Number(owner?.delayDays ?? 0) || 0;
    const next = Math.min(
      MAX_DELAY_DAYS,
      Math.max(MIN_DELAY_DAYS, current + delta),
    );
    applyToLevel({
      delayDays: next,
      delayHours: owner?.delayHours ?? 0,
      scheduleTime: undefined,
    });
  };

  const removeDelay = () => {
    applyToLevel({
      delayDays: undefined,
      delayHours: undefined,
      scheduleTime: undefined,
    });
    dispatch(setErrors({ delay: "" }));
  };

  if (!actionable.length) return null;

  const renderPill = () => {
    // An absolute wait has no day count to step, so the pill shows the date.
    if (owner?.scheduleTime) {
      return (
        <Box sx={styles.delayPill}>
          <ClockGlyph sx={styles.delayClock} />
          <Box component="span" sx={styles.delayText}>
            Wait until{" "}
            {convertESTtoUserLocalDateAndTime(
              owner.scheduleTime,
              "MMM D, YYYY h:mm A",
              "MM/DD/YYYY HH:mm:ss",
            )}
          </Box>
          <Box sx={styles.delaySteps}>
            <Button
              disableRipple
              aria-label="Edit delay date and time"
              onClick={onEdit}
              sx={styles.delayStep}
            >
              <ClockGlyph sx={styles.delayRemoveIcon} />
            </Button>
            <IconButton
              disableRipple
              aria-label="Remove delay"
              onClick={removeDelay}
              sx={styles.delayRemove}
            >
              <CloseGlyph sx={styles.delayRemoveIcon} />
            </IconButton>
          </Box>
        </Box>
      );
    }

    const days = Number(owner?.delayDays ?? 0) || 0;
    const hours = Number(owner?.delayHours ?? 0) || 0;

    return (
      <Box sx={styles.delayPill}>
        <ClockGlyph sx={styles.delayClock} />
        <Box component="span" sx={styles.delayText}>
          Wait {days} day(s){hours ? ` ${hours} hour(s)` : ""} before next level
        </Box>
        <Box sx={styles.delaySteps}>
          <Button
            disableRipple
            aria-label="Decrease delay"
            onClick={() => stepDays(-1)}
            sx={styles.delayStep}
          >
            −
          </Button>
          <Button
            disableRipple
            aria-label="Increase delay"
            onClick={() => stepDays(1)}
            sx={styles.delayStep}
          >
            +
          </Button>
          <IconButton
            disableRipple
            aria-label="Remove delay"
            onClick={removeDelay}
            sx={styles.delayRemove}
          >
            <CloseGlyph sx={styles.delayRemoveIcon} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={styles.delaySlot}>
      <Box sx={styles.delayStem} />
      {owner ? (
        renderPill()
      ) : (
        <Tooltip
          title={isAvailable ? "" : tooltip}
          arrow
          disableHoverListener={isAvailable}
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <Button
              disableRipple
              disabled={!isAvailable}
              onClick={onEdit}
              sx={styles.delayAdd}
            >
              <ClockGlyph sx={styles.delayAddIcon} />
              Add delay
            </Button>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default SignerDelaySlot;
